import { ReactNode } from "react";
import { HomeOutlined, OrderedListOutlined, FileTextOutlined, EyeOutlined } from "@ant-design/icons";

export interface BreadcrumbMeta {
  label: string;
  icon?: ReactNode;
  // 쿼리파라미터 동적 라벨
  labelFromQuery?: string;
  parent?: string;
  useNavigateBack?: boolean;
}

export interface RouteConfig {
  path: string;
  breadcrumb?: BreadcrumbMeta;
}

export const routeConfigs: RouteConfig[] = [
  {
    path: "/",
    breadcrumb: {
      label: "Home",
      icon: <HomeOutlined />,
    },
  },
  {
    path: "/category",
    breadcrumb: {
      label: "Category",
      icon: <OrderedListOutlined />,
    },
  },
  {
    path: "/post/:id",
    breadcrumb: {
      label: "Post",
      icon: <FileTextOutlined />,
      labelFromQuery: "title", // ?title=항목명
      // parent는 쿼리파라미터 from으로 동적 결정
      useNavigateBack: true, // 뒤로가기 사용
    },
  },
  {
    path: "/viewer/:slide",
    breadcrumb: {
      label: "Viewer",
      icon: <EyeOutlined />,
      labelFromQuery: "slideName", // ?slideName=슬라이드파일명
      useNavigateBack: true,
    },
  },
  {
    path: "/demo/viewer/:id",
    breadcrumb: {
      label: "Viewer",
      icon: <EyeOutlined />,
      labelFromQuery: "slideName",
    },
  },
  {
    path: "/demo/consultation/:boardId",
    breadcrumb: {
      label: "Consultation",
      icon: <FileTextOutlined />,
      labelFromQuery: "title",
    },
  },
  {
    path: "/viewersample2/:slide",
    breadcrumb: {
      label: "Viewer",
      icon: <EyeOutlined />,
      labelFromQuery: "name",
      parent: "/post/:id",
      useNavigateBack: true,
    },
  },
  {
    path: "/slideboard/:boardId",
    breadcrumb: {
      label: "Board",
      icon: <FileTextOutlined />,
      labelFromQuery: "title",
      parent: "/category",
      useNavigateBack: true,
    },
  },
  // [통합] /consultation -> /post 경로 통합 (롤백 시 아래 주석 해제)
  // {
  //   path: "/consultation/:boardId",
  //   breadcrumb: {
  //     label: "Consultation",
  //     icon: <FileTextOutlined />,
  //     labelFromQuery: "title",
  //     useNavigateBack: true,
  //   },
  // },
  {
    path: "/search",
    breadcrumb: {
      label: "Search",
    },
  },
  {
    path: "/mission",
    breadcrumb: {
      label: "미션과 비전",
    },
  },
  {
    path: "/publication",
    breadcrumb: {
      label: "Publication",
    },
  },
  {
    path: "/committee",
    breadcrumb: {
      label: "운영진 및 자문위원",
    },
  },
  {
    path: "/tribute",
    breadcrumb: {
      label: "기리는 분들",
    },
  },
  {
    path: "/encouragement",
    breadcrumb: {
      label: "격려의 글",
    },
  },
  {
    path: "/gallery",
    breadcrumb: {
      label: "아틀라스 갤러리",
    },
  },
  // /gallery 신규 디자인 시안 — 시안 A 채택으로 라우트와 함께 숨김 (다시 비교하려면 주석 해제)
  // {
  //   path: "/gallery-draft/cover",
  //   breadcrumb: {
  //     label: "갤러리 디자인 시안 A",
  //     parent: "/gallery",
  //   },
  // },
  // {
  //   path: "/gallery-draft/mosaic",
  //   breadcrumb: {
  //     label: "갤러리 디자인 시안 B",
  //     parent: "/gallery",
  //   },
  // },
  // {
  //   path: "/gallery-draft/shelf",
  //   breadcrumb: {
  //     label: "갤러리 디자인 시안 C",
  //     parent: "/gallery",
  //   },
  // },
  {
    path: "/post-list/:categoryId",
    breadcrumb: {
      label: "Organ",
      labelFromQuery: "name",
      parent: "/gallery",
    },
  },
  {
    path: "/portal",
    breadcrumb: {
      label: "질환 카테고리",
      labelFromQuery: "categoryName",
    },
  },
  {
    path: "/write",
    breadcrumb: {
      label: "게시글 등록",
      icon: <FileTextOutlined />,
      useNavigateBack: true,
    },
  },
  {
    path: "/conference",
    breadcrumb: {
      label: "소아병리연구회 집담회",
    },
  },
  {
    path: "/reference",
    breadcrumb: {
      label: "자료실 및 게시판",
      labelFromQuery: "referenceTitle",
    },
  },
  {
    path: "/qna",
    breadcrumb: {
      label: "Q&A",
      labelFromQuery: "qnaTitle",
    },
  },
  {
    path: "/dedicated",
    breadcrumb: {
      label: "Board",
      labelFromQuery: "board",
    },
  },
  {
    path: "/admin",
    breadcrumb: {
      label: "관리자",
    },
  },
  {
    path: "/admin/boards",
    breadcrumb: {
      label: "게시판 권한 관리",
      parent: "/admin",
    },
  },
  {
    path: "/admin/users",
    breadcrumb: {
      label: "유저 관리",
      parent: "/admin",
    },
  },
  {
    path: "/admin/signup",
    breadcrumb: {
      label: "회원가입 관리",
      parent: "/admin",
    },
  },
  {
    path: "/admin/category",
    breadcrumb: {
      label: "카테고리 편집",
      parent: "/admin",
      labelFromQuery: "categoryName",
    },
  },
  {
    path: "/mypage",
    breadcrumb: {
      label: "마이페이지",
    },
  },
];

// 경로 매칭 헬퍼 함수
export const matchRoute = (pathname: string): RouteConfig | undefined => {
  return routeConfigs.find((config) => {
    const pattern = config.path
      .replace(/:[^/]+/g, "([^/]+)")
      .replace(/\//g, "\\/");
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(pathname);
  });
};

// 경로에서 파라미터 추출
export const extractParams = (pathname: string, configPath: string): Record<string, string> => {
  const paramNames = configPath.match(/:[^/]+/g)?.map((p) => p.slice(1)) || [];
  const pattern = configPath
    .replace(/:[^/]+/g, "([^/]+)")
    .replace(/\//g, "\\/");
  const regex = new RegExp(`^${pattern}$`);
  const match = pathname.match(regex);

  if (!match) return {};

  const params: Record<string, string> = {};
  paramNames.forEach((name, index) => {
    params[name] = match[index + 1];
  });

  return params;
};
